import { IconLayoutDashboard } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Text } from '@fragments';
import TabsSelector from '@components/TabsSelector';

import CommunityHeroCard from '../CommunityHeroCard';
import RecentSubscriptionsCard from '../RecentSubscriptionsCard';
import GrowthCard from '../GrowthCard';

/**
 * OwnerOverview — the "first card": only rendered when the user owns at least
 * one community. With several owned communities a TabsSelector switches which
 * one it describes (selection persisted by the hook).
 *
 * A bento of three columns proportioned 2 · 3 · 2: identity · Crescimento ·
 * recent subscriptions. All three are `split` Cards, so each header, body
 * and footer is a block of its own, all a seam apart — identity has no
 * header, so it reads as body + actions. The corner choreography (outer /
 * column / inner radii) lives in the Home styles.
 */
export default function OwnerOverview({
    ownerCommunities,
    activeCommunity,
    activeCommunityId,
    selectCommunity,
    activeOverview,
    communityStats,
    openCommunity,
    openSettings,
    shareCommunity,
}) {
    const { t } = useTranslation('dashboard');

    const showTabs = ownerCommunities.length > 1;

    const tabs = ownerCommunities.map(c => ({ key: String(c.id), label: c.name }));

    return (
        <section className="home__section">
            <div className="home__section-bar">
                <div className="home__section-heading">
                    <Text.caption weight={500} contrast={6} left={<IconLayoutDashboard />}>
                        {t('home.overview.eyebrow')}
                    </Text.caption>
                    <Text.h2 weight={500} contrast={9}>
                        {showTabs ? t('home.overview.titleMultiple') : activeCommunity?.name || t('home.overview.titleSingle')}
                    </Text.h2>
                </div>

                {showTabs && (
                    <TabsSelector
                        className="home__tabs mt-2"
                        tabs={tabs}
                        value={activeCommunityId ? String(activeCommunityId) : tabs[0]?.key}
                        onChange={selectCommunity}
                    />
                )}
            </div>

            <div className="home__owner-bento">
                <div className="home__owner-bento-identity">
                    <CommunityHeroCard
                        split
                        community={activeCommunity}
                        plansCount={activeOverview.plansCount}
                        stats={activeCommunity ? communityStats[activeCommunity.id] : null}
                        loading={activeOverview.loading}
                        onOpen={openCommunity}
                        onSettings={openSettings}
                        onShare={shareCommunity}
                    />
                </div>

                <div className="home__owner-bento-main">
                    <GrowthCard
                        split
                        growth={activeOverview.growth}
                        loading={activeOverview.loading}
                        communityId={activeCommunityId}
                    />
                </div>

                {/* The side FOLLOWS the row height instead of driving it: the
                    wrapper is what the grid sizes and the card fills it
                    absolutely, so the list scrolls inside the height the
                    identity column sets. */}
                <div className="home__owner-bento-side">
                    <RecentSubscriptionsCard
                        split
                        subscriptions={activeOverview.subscriptions}
                        loading={activeOverview.loading}
                    />
                </div>
            </div>
        </section>
    );
}
